import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { 
    registrarEgreso,
    obtenerEgresos, 
    editarEgreso,
    eliminarEgreso,
    obtenerEgresoPorId
} from '../../services/FinanzasService';
import { formatearFechaLocal, obtenerFechaActualLocal } from '../../utils/dateUtils';
import { obtenerCentro } from '../../services/centroService';
import { 
    Plus,
    Edit2,
    Trash2,
    Search,
    Filter,

    X,
    Save,
    SlidersHorizontal,
    FileText,
    CalendarDays,
    User,
    CheckCircle,
    Clock,
    Loader
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

Modal.setAppElement('#root');

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
    });
};

// Custom Button Component
const Button = ({ 
  children, 
  onClick, 
  className = '', 
  variant = 'primary', 
  size = 'default',
  disabled = false
}) => {
  const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
  
  const variantStyles = {
    primary: 'bg-indigo-500 text-white hover:bg-indigo-600 disabled:bg-gray-400',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-50 disabled:text-gray-400',
    green: 'bg-emerald-500 text-white hover:bg-emerald-600 disabled:bg-gray-400',
    danger: 'bg-rose-500 text-white hover:bg-rose-600 disabled:bg-gray-400'
  };

  const sizeStyles = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-1.5 text-xs',
    lg: 'px-6 py-3 text-base',
    icon: 'p-2',
    xs: 'p-1 text-xs'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseStyles} 
        ${variantStyles[variant]} 
        ${sizeStyles[size]} 
        ${className}
        ${disabled ? 'cursor-not-allowed opacity-50' : ''}
      `}
    >
      {children}
    </button>
  );
};

const Egresos = () => {
    const [egresos, setEgresos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [filtroActivo, setFiltroActivo] = useState('fecha');
    const [filtros, setFiltros] = useState({
        fechaInicio: '',
        fechaFin: '',
        tipoEgreso: '',
        estado: ''
    });
    const [egresoActual, setEgresoActual] = useState({
        id_centro: '',
        monto: '',
        concepto: '',
        tipo_egreso: 'Servicios',
        estado: 'Pendiente',
        fecha_pago: null
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [idCentro, setIdCentro] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        // Primero cargar el centro, luego los egresos
        const inicializarDatos = async () => {
            await cargarCentro();
            cargarEgresos();
        };
        inicializarDatos();
    }, []);

    const cargarCentro = async () => {
        try {
            const response = await obtenerCentro();
            // Verificar si hay datos en response.data (objeto) o response.data (array)
            if (response && response.data) {
                // Si data es un array con elementos
                if (Array.isArray(response.data) && response.data.length > 0) {
                    const centro = response.data[0];
                    setIdCentro(centro.id_centro);
                    setEgresoActual(prev => ({...prev, id_centro: centro.id_centro}));
                } 
                // Si data es un objeto con id_centro
                else if (response.data.id_centro) {
                    const centro = response.data;
                    setIdCentro(centro.id_centro);
                    setEgresoActual(prev => ({...prev, id_centro: centro.id_centro}));
                }
            }
        } catch (error) {
            showErrorToast('Error al cargar datos del centro');
        }
    };

    const cargarEgresos = async () => {
        setLoading(true);
        try {
            const response = await obtenerEgresos();
            if (response && response.egresos) {
                const egresosOrdenados = response.egresos.sort((a, b) => {
                    if (a.estado === 'Completado' && b.estado === 'Pendiente') return -1;
                    if (a.estado === 'Pendiente' && b.estado === 'Completado') return 1;
                    return 0;
                });
                setEgresos(egresosOrdenados);
            } else {
                setEgresos([]);
            }
        } catch (error) {
            showErrorToast('Error al obtener datos de la base de datos');
            setEgresos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (parseFloat(egresoActual.monto) <= 0) {
            showWarningToast('El monto debe ser mayor a 0');
            return;
        }

        // Verificar que el id_centro exista
        if (!idCentro) {
            showWarningToast('Error: No se ha cargado correctamente el centro. Intente recargar la página.');
            return;
        }

        try {
            const egresoToSubmit = {
                ...egresoActual,
                id_centro: idCentro,
                fecha_pago: egresoActual.estado === 'Completado' ? obtenerFechaActualLocal() : null
            };

            if (egresoToSubmit.fecha_pago) {
                const fechaPago = new Date();
                // Ajustar a zona horaria de Ecuador (UTC-5)
                fechaPago.setHours(fechaPago.getHours() - 5);
                egresoToSubmit.fecha_pago = formatearFechaLocal(fechaPago);
            }
            
            if (!modoEdicion) {
                await registrarEgreso(egresoToSubmit);
                showSuccessToast('Egreso registrado exitosamente');
            } else {
                await editarEgreso(egresoToSubmit.id_egreso, egresoToSubmit);
                showSuccessToast('Egreso actualizado exitosamente');
            }
            cargarEgresos();
            cerrarModal();
        } catch (error) {
            showErrorToast('Error al procesar el egreso');
        }
    };

    const handleDelete = async (id) => {
        const egresoAEliminar = egresos.find(e => e.id_egreso === id);
        if (egresoAEliminar?.estado === 'Completado') {
            showWarningToast('No se pueden eliminar egresos completados');
            return;
        }

        toast.warn(
            <div>
                <p className="font-bold mb-2">¿Está seguro de eliminar este egreso?</p>
                <p className="mb-4">Esta acción no se puede deshacer.</p>
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={() => toast.dismiss()}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                await eliminarEgreso(id);
                                showSuccessToast('Egreso eliminado exitosamente');
                                cargarEgresos();
                            } catch (error) {
                                showErrorToast('Error al eliminar el egreso');
                            }
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Eliminar
                    </button>
                </div>
            </div>,
            {
                position: "top-center",
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
                closeButton: false,
                className: "rounded-lg",
            }
        );
    };

    const handleToggleEstado = async (egreso) => {
        if (egreso.estado === 'Completado') {
            showWarningToast('No se puede modificar un egreso completado');
            return;
        }

        try {
            const nuevoEstado = 'Completado';
            const fechaPago = new Date();
            // Ajustar a zona horaria de Ecuador (UTC-5)
            fechaPago.setHours(fechaPago.getHours() - 5);
            await editarEgreso(egreso.id_egreso, { 
                ...egreso, 
                estado: nuevoEstado, 
                fecha_pago: formatearFechaLocal(fechaPago)
            });
            showSuccessToast('Estado actualizado exitosamente');
            cargarEgresos();
        } catch (error) {
            showErrorToast('Error al actualizar el estado');
        }
    };

    const abrirModalCrear = () => {
        setEgresoActual({
            id_centro: idCentro,
            monto: '',
            concepto: '',
            tipo_egreso: 'Servicios',
            estado: 'Pendiente',
            fecha_pago: null
        });
        setModoEdicion(false);
        setModalAbierto(true);
    };

    const abrirModalEditar = (egreso) => {
        if (egreso.estado === 'Completado') {
            showWarningToast('No se pueden editar egresos completados');
            return;
        }
        setEgresoActual({...egreso, id_centro: idCentro});
        setModoEdicion(true);
        setModalAbierto(true);
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setEgresoActual({
            id_centro: idCentro,
            monto: '',
            concepto: '',
            tipo_egreso: 'Servicios',
            estado: 'Pendiente',
            fecha_pago: null
        });
    };

    const limpiarFiltros = () => {
        setFiltros({
            fechaInicio: '',
            fechaFin: '',
            tipoEgreso: '',
            estado: ''
        });
        setSearchTerm('');
        setFiltroActivo('fecha');
    };

    const renderFiltroActivo = () => {
        switch(filtroActivo) {
            case 'fecha':
                return (
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                            <input
                                type="date"
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                                value={filtros.fechaInicio}
                                onChange={(e) => setFiltros({...filtros, fechaInicio: e.target.value})}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
                            <input
                                type="date"
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                                value={filtros.fechaFin}
                                onChange={(e) => setFiltros({...filtros, fechaFin: e.target.value})}
                            />
                        </div>
                    </div>
                );
            case 'tipo':
                return (
                    <select
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                        value={filtros.tipoEgreso}
                        onChange={(e) => setFiltros({...filtros, tipoEgreso: e.target.value})}
                    >
                        <option value="">Todos los tipos</option>
                        <option value="Servicios">Servicios</option>
                        <option value="Materiales">Materiales</option>
                        <option value="Salarios">Salarios</option>
                        <option value="Otros">Otros</option>
                    </select>
                );
            case 'estado':
                return (
                    <select
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                        value={filtros.estado}
                        onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                    >
                        <option value="">Todos los estados</option>
                        <option value="Pendiente">Pendiente</option>
                        <option value="Completado">Completado</option>
                    </select>
                );
            case 'busqueda':
                return (
                    <div className="relative">
                        <input
                            type="text"
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Buscar por concepto..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                    </div>
                );
            default:
                return null;
        }
    };

    const egresosFiltrados = egresos && egresos.length ? egresos.filter(egreso => {
        if (!egreso) return false;
        
        const cumpleFiltroFecha = (!filtros.fechaInicio || new Date(egreso.fecha_pago || egreso.fecha) >= new Date(filtros.fechaInicio)) &&
                                 (!filtros.fechaFin || new Date(egreso.fecha_pago || egreso.fecha) <= new Date(filtros.fechaFin));
        const cumpleFiltroTipo = !filtros.tipoEgreso || egreso.tipo_egreso === filtros.tipoEgreso;
        const cumpleFiltroEstado = !filtros.estado || egreso.estado === filtros.estado;
        const cumpleBusqueda = egreso.concepto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              egreso.tipo_egreso?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return cumpleFiltroFecha && cumpleFiltroTipo && cumpleFiltroEstado && cumpleBusqueda;
    }).sort((a, b) => {
        // Siempre ordenar primero los completados, luego los pendientes
        if (a.estado === 'Completado' && b.estado === 'Pendiente') return -1;
        if (a.estado === 'Pendiente' && b.estado === 'Completado') return 1;
        return 0;
    }) : [];

    return (
        <div className="min-h-screen bg-gray-50 p-2 sm:p-6">
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                className="mt-16"
                style={{ zIndex: 9999 }}
            />
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                                <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                                Gestión de Egresos
                            </h1>
                            <p className="text-gray-500 text-sm mt-1">
                                Administra los egresos del centro
                            </p>
                        </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                <Button
                                    onClick={abrirModalCrear}
                                    variant="primary"
                                    size="default"
                                    className="flex items-center justify-center gap-2"
                                >
                                    <Plus size={16} />
                                    Agregar Egreso
                                </Button>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-col gap-2">
                            {/* Filtros en móvil */}
                            <div className="block sm:hidden">
                                <select
                                    onChange={(e) => setFiltroActivo(e.target.value)}
                                    value={filtroActivo}
                                    className="w-full rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm"
                                >
                                    <option value="fecha">Fecha</option>
                                    <option value="tipo">Tipo</option>
                                    <option value="estado">Estado</option>
                                    <option value="busqueda">Buscar</option>
                                </select>
                            </div>

                            {/* Filtros en desktop */}
                            <div className="hidden sm:flex gap-2">
                                <button
                                    onClick={() => setFiltroActivo('fecha')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        filtroActivo === 'fecha'
                                        ? 'bg-indigo-50 text-indigo-600'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    Fecha
                                </button>
                                <button
                                    onClick={() => setFiltroActivo('tipo')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        filtroActivo === 'tipo'
                                        ? 'bg-indigo-50 text-indigo-600'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    Tipo
                                </button>
                                <button
                                    onClick={() => setFiltroActivo('estado')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        filtroActivo === 'estado'
                                        ? 'bg-indigo-50 text-indigo-600'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    Estado
                                </button>
                                <button
                                    onClick={() => setFiltroActivo('busqueda')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        filtroActivo === 'busqueda'
                                        ? 'bg-indigo-50 text-indigo-600'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    Buscar
                                </button>
                    </div>

                    {filtroActivo && (
                                <div className="mt-2">
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    {renderFiltroActivo()}
                                </div>
                                {(filtros.fechaInicio || filtros.fechaFin || filtros.tipoEgreso || filtros.estado || searchTerm) && (
                                    <button
                                        onClick={limpiarFiltros}
                                                className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 whitespace-nowrap"
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                    </div>
                </div>

                {/* Vista móvil */}
                <div className="block sm:hidden space-y-4">
                    {egresosFiltrados.map((egreso) => (
                        <div key={egreso.id_egreso} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <div className="font-medium text-gray-900">
                                        {egreso.concepto}
                                    </div>
                                    <div className="text-sm text-red-600 mt-1">
                                        ${parseFloat(egreso.monto).toFixed(2)}
                                    </div>
                                </div>
                                <Button
                                    onClick={() => handleToggleEstado(egreso)}
                                    variant={egreso.estado === 'Completado' ? 'green' : 'ghost'}
                                    size="sm"
                                    className="flex items-center gap-1"
                                    disabled={egreso.estado === 'Completado'}
                                >
                                    {egreso.estado === 'Completado' ? 'Pagado' : 'Pendiente'}
                                </Button>
                            </div>

                            <div className="space-y-2 text-sm text-gray-600">
                                <div className="flex justify-between">
                                    <span>Fecha:</span>
                                    <span>{egreso.estado === 'Completado' ? new Date(egreso.fecha_pago + 'T00:00:00').toLocaleDateString() : '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Tipo:</span>
                                    <span>{egreso.tipo_egreso}</span>
                                </div>
                            </div>

                            {egreso.estado !== 'Completado' && (
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        onClick={() => abrirModalEditar(egreso)}
                                        className="p-2 text-gray-400 hover:text-indigo-500 transition-colors"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(egreso.id_egreso)}
                                        className="p-2 text-gray-400 hover:text-rose-500 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
            </div>

                {/* Vista desktop */}
                <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                            <tbody className="divide-y divide-gray-200">
                        {egresosFiltrados.map((egreso) => (
                                    <tr key={egreso.id_egreso} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {egreso.estado === 'Completado' ? new Date(egreso.fecha_pago + 'T00:00:00').toLocaleDateString() : '-'}
                                </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {egreso.concepto}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-red-600 font-medium">
                                            ${parseFloat(egreso.monto).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {egreso.tipo_egreso}
                                        </td>
                                <td className="px-6 py-4">
                                    <Button
                                        onClick={() => handleToggleEstado(egreso)}
                                        variant={egreso.estado === 'Completado' ? 'green' : 'ghost'}
                                        size="sm"
                                        className="flex items-center gap-1"
                                        disabled={egreso.estado === 'Completado'}
                                    >
                                        {egreso.estado === 'Completado' ? (
                                            <>
                                                <CheckCircle size={14} />
                                                Pagado
                                            </>
                                        ) : (
                                            <>
                                                <Clock size={14} />
                                                Pendiente
                                            </>
                                        )}
                                    </Button>
                                </td>
                                <td className="px-6 py-4">
                                            <div className="flex gap-2">
                                        {egreso.estado !== 'Completado' && (
                                            <>
                                                        <button
                                                    onClick={() => abrirModalEditar(egreso)}
                                                            className="p-1 text-gray-400 hover:text-indigo-500 transition-colors"
                                                    title="Editar"
                                                >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                    onClick={() => handleDelete(egreso.id_egreso)}
                                                            className="p-1 text-gray-400 hover:text-rose-500 transition-colors"
                                                    title="Eliminar"
                                                >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                    </div>
                </div>
            </div>

            {/* Paginación */}
            <div className="mt-4 px-4 py-3 sm:px-6 sm:py-4 bg-white rounded-lg border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex justify-between sm:hidden">
                        <Button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                        >
                            Anterior
                        </Button>
                        <span className="text-xs text-gray-700">
                            Página {currentPage} de {Math.ceil(egresosFiltrados.length / 10)}
                        </span>
                        <Button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(egresosFiltrados.length / 10)))}
                            disabled={currentPage === Math.ceil(egresosFiltrados.length / 10)}
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                        >
                            Siguiente
                        </Button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * 10 + 1, egresosFiltrados.length)}</span> a{' '}
                                <span className="font-medium">
                                    {Math.min(currentPage * 10, egresosFiltrados.length)}
                                </span>{' '}
                                de <span className="font-medium">{egresosFiltrados.length}</span> resultados
                            </p>
                        </div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                            {[...Array(Math.ceil(egresosFiltrados.length / 10))].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => setCurrentPage(i + 1)}
                                    className={`relative inline-flex items-center px-3 sm:px-4 py-2 border text-xs sm:text-sm font-medium
                                        ${currentPage === i + 1 
                                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                        }
                                        ${i === 0 ? 'rounded-l-md' : ''}
                                        ${i === Math.ceil(egresosFiltrados.length / 10) - 1 ? 'rounded-r-md' : ''}
                                    `}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            <Modal
                isOpen={modalAbierto}
                onRequestClose={cerrarModal}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-lg p-4 sm:p-6 w-[95%] sm:w-full max-w-2xl mx-2"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            >
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-medium text-gray-900">
                        {modoEdicion ? 'Editar Egreso' : 'Nuevo Egreso'}
                    </h2>
                    <Button
                        onClick={cerrarModal}
                        variant="ghost"
                        size="icon"
                    >
                        <X size={20} />
                    </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                Monto
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                required
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs sm:text-sm"
                                value={egresoActual.monto}
                                onChange={(e) => setEgresoActual({...egresoActual, monto: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                Tipo de Egreso
                            </label>
                            <select
                                required
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs sm:text-sm"
                                value={egresoActual.tipo_egreso}
                                onChange={(e) => setEgresoActual({...egresoActual, tipo_egreso: e.target.value})}
                            >
                                <option value="Servicios">Servicios</option>
                                <option value="Materiales">Materiales</option>
                                <option value="Salarios">Salarios</option>
                                <option value="Otros">Otros</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                Concepto
                            </label>
                            <textarea
                                required
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs sm:text-sm"
                                value={egresoActual.concepto}
                                onChange={(e) => setEgresoActual({...egresoActual, concepto: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                Estado
                            </label>
                            <select
                                required
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs sm:text-sm"
                                value={egresoActual.estado}
                                onChange={(e) => {
                                    const nuevoEstado = e.target.value;
                                    setEgresoActual({
                                        ...egresoActual, 
                                        estado: nuevoEstado,
                                        fecha_pago: nuevoEstado === 'Completado' ? (egresoActual.fecha_pago || obtenerFechaActualLocal()) : null
                                    });
                                }}
                            >
                                <option value="Pendiente">Pendiente</option>
                                <option value="Completado">Completado</option>
                            </select>
                        </div>
                        {egresoActual.estado === 'Completado' && (
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Fecha de Pago
                                </label>
                                <input
                                    type="date"
                                    required
                                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs sm:text-sm"
                                    value={egresoActual.fecha_pago}
                                    onChange={(e) => setEgresoActual({...egresoActual, fecha_pago: e.target.value})}
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                        <Button
                            type="button"
                            onClick={cerrarModal}
                            variant="ghost"
                            size="sm"
                            className="text-xs sm:text-sm"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            className="text-xs sm:text-sm"
                        >
                            {modoEdicion ? 'Actualizar' : 'Registrar'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default Egresos;
